import Logger from '@/logger'
import ServiceFactory from '@/services/service-factory'
import SchemeServicingManager from '@/scheme-servicing-manager'
import Store from '@/store'

/*
 * The UserManager deals with user profile work that doesn't interact with authentication.
 */
const UserManager = {

    /**
     * Confirms contact has been made with a user.
     * @async
     * @returns {Promise<User>} The updated user data.
     */
    async confirmContactAsync() {
        try {
            const service = ServiceFactory.contact()
            const operationState = await service.setContactMadeAsync()
            if (!operationState.succeeded)
                operationState.throw()
            else
                return await UserManager.loadProfileDataAsync()
        } catch (err) {
            Logger.log(`Unable to confirm that contact has been made: ${ err.message }`, 'My Account')
            throw err
        }
    },

    /**
     * Grant consent.
     * @async
     * @param {string} consentType The type of consent - either 'marketing' or 'evaluation'
     * @returns {Promise<User>} The updated user data.
     */
    async consentToAsync(consentType) {
        try {
            const service = ServiceFactory.profile()
            const operationState = await service.updateConsentAsync(consentType, true)
            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            Logger.log(`Unable to consent to ${ consentType }: ${ err.message }`, 'My Account')
            throw err
        }
    },

    /**
     * Loads the user data.
     * @async
     * @returns {Promise<User>} The user data.
     */
    async loadProfileDataAsync() {
        try {
            const service = ServiceFactory.profile()
            const operationState = await service.loadUserInfoAsync()

            if (!operationState.succeeded) {
                operationState.throw()
            } else {
                if (operationState.data.partnerLinkId)
                    await SchemeServicingManager.updatePartnerServicesAsync(operationState.data.partnerLinkId)

                return operationState.data
            }
        } catch (err) {
            Logger.log(`Unable to load user info: ${ err.message }`, 'My Account')
            throw err
        }
    },

    /**
     * Withdraw consent.
     * @async
     * @param {string} consentType The type of consent - either 'marketing' or 'evaluation'
     * @returns {Promise<User>} The updated user data.
     */
    async withdrawConsentForAsync(consentType) {
        try {
            const service = ServiceFactory.profile()
            const operationState = await service.updateConsentAsync(consentType, false)
            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            Logger.log(`Unable to withdraw ${ consentType } consent: ${ err.message }`, 'My Account')
            throw err
        }
    },

    /**
     * True if the account settings feature should be enabled for the current user.
     * @returns {boolean}
     */
    enableAccountSettings() {
        const authContext = Store.state.authContext

        return !!authContext && authContext.type === 'account'
    },

    /**
     * True if current user is external.
     * @returns {boolean}
     */
    isExternalUser() {
        const authContext = Store.state.authContext
        return !!authContext && authContext.type === 'external'
    },
}

export default UserManager
